// ABOUTME: Modal for adding new adult family members with invitation or direct account creation
// ABOUTME: Supports email invitation or direct account creation with password

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Chip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { AddAdultFormState } from '../types';
import { RoleSuggestion } from '../../../types/api';

interface AddAdultModalProps {
  open: boolean;
  form: AddAdultFormState;
  roleSuggestions: RoleSuggestion[];
  loadingRoles: boolean;
  showPasswords: { password: boolean; confirmPassword: boolean };
  error: string | null;
  onClose: () => void;
  onChange: (form: AddAdultFormState) => void;
  onTogglePasswordVisibility: (field: 'password' | 'confirmPassword') => void;
  onSubmit: () => Promise<void>;
}

const AddAdultModal: React.FC<AddAdultModalProps> = ({
  open,
  form,
  roleSuggestions,
  loadingRoles,
  showPasswords,
  error,
  onClose,
  onChange,
  onTogglePasswordVisibility,
  onSubmit
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Family Member</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="First Name"
            fullWidth
            required
            value={form.firstName}
            onChange={(e) => onChange({ ...form, firstName: e.target.value })}
          />
          <TextField
            label="Last Name"
            fullWidth
            required
            value={form.lastName}
            onChange={(e) => onChange({ ...form, lastName: e.target.value })}
          />
          <TextField
            label="Email"
            type="email"
            fullWidth
            required
            value={form.email}
            onChange={(e) => onChange({ ...form, email: e.target.value })}
          />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Role
            </Typography>
            {loadingRoles ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {roleSuggestions.map((suggestion) => (
                  <Chip
                    key={suggestion.id}
                    label={suggestion.roleName}
                    onClick={() => onChange({ ...form, role: suggestion.roleName })}
                    variant={form.role === suggestion.roleName ? 'filled' : 'outlined'}
                    color={form.role === suggestion.roleName ? 'primary' : 'default'}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: form.role === suggestion.roleName ? 'primary.dark' : 'action.hover'
                      }
                    }}
                  />
                ))}
              </Box>
            )}
            {!form.role && (
              <Typography variant="caption" color="error">
                Please select a role
              </Typography>
            )}
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={form.isPrimaryCaregiver}
                onChange={(e) => onChange({ ...form, isPrimaryCaregiver: e.target.checked })}
              />
            }
            label="Primary Caregiver"
          />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Invitation Method
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.invitationMethod === 'email'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onChange({ ...form, invitationMethod: 'email' });
                      }
                    }}
                  />
                }
                label="Send email invitation"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.invitationMethod === 'direct'}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onChange({ ...form, invitationMethod: 'direct' });
                      }
                    }}
                  />
                }
                label="Add without email (I'll share login details)"
              />
            </FormGroup>
          </Box>

          {form.invitationMethod === 'direct' && (
            <>
              <TextField
                label="Password"
                type={showPasswords.password ? 'text' : 'password'}
                fullWidth
                required
                value={form.password}
                onChange={(e) => onChange({ ...form, password: e.target.value })}
                helperText="Must be between 8 and 64 characters"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => onTogglePasswordVisibility('password')}
                        edge="end"
                      >
                        {showPasswords.password ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Confirm Password"
                type={showPasswords.confirmPassword ? 'text' : 'password'}
                fullWidth
                required
                value={form.confirmPassword}
                onChange={(e) => onChange({ ...form, confirmPassword: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => onTogglePasswordVisibility('confirmPassword')}
                        edge="end"
                      >
                        {showPasswords.confirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Personal Mantra (optional)"
                fullWidth
                multiline
                rows={2}
                value={form.mantra}
                onChange={(e) => onChange({ ...form, mantra: e.target.value })}
              />
              <TextField
                label="What do you love about your family? (optional)"
                fullWidth
                multiline
                rows={2}
                value={form.familyLoveQuestion}
                onChange={(e) => onChange({ ...form, familyLoveQuestion: e.target.value })}
              />
              <TextField
                label="What does your family need from you? (optional)"
                fullWidth
                multiline
                rows={2}
                value={form.familyNeedQuestion}
                onChange={(e) => onChange({ ...form, familyNeedQuestion: e.target.value })}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={onSubmit}>Add Member</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAdultModal;
